import { redirectToLegacyPage, useCurrentAgreement } from '@economic/react-app';
import { IconName } from '@economic/taco';
import { t } from '@lingui/macro';
import { t as oldT } from '../../components/l10n/l10n';

export interface ClientNotification {
    id: string;
    type: NotificationType;
    createdAt: Date;
    data: object;
    content?: {
        text?: string;
        href?: string;
    };
    read: boolean;
    groupId?: string;
}

export enum NotificationType {
    Custom = 0,
    DocumentReceived = 1,
    BankImportCompleted = 2,
    NoteFollowUpDateReached = 3,
    SalesInvoiceOverdue = 4,
    RequestedDocumentUploaded = 6,
    DocumentRequestRejected = 8,
    PurchaseInvoiceOverdue = 10,
    SalesInvoiceSentError = 11,
    NewInvoiceFromEconomic = 12,
    DocumentSentForApproval = 15,
    ElectronicDocumentReceived = 21,
}

export interface NotificationDetails {
    name: string;
    description?: string;
    icon: IconName;
    textId?: string;
    textIdPlural?: string;
    getTextId?: (notification: ClientNotification) => string;
    getRoute?: (notification: ClientNotification) => string;
    getLegacyPageRedirect?: (notification: ClientNotification) => string | null;
}

/**
 * This is being used as a function instead of an object because `t` macro gets executed
 * before the locale is fully loaded, resulting in text not being translated. More details
 * here: https://js-lingui-git-next-lingui.vercel.app/tutorials/react-patterns#translations-outside-react-components
 */
export const getNotificationDetails = (): Record<NotificationType, NotificationDetails> => ({
    [NotificationType.Custom]: {
        name: `NOTIFICATIONS_TYPES_CUSTOM_NAME`,
        icon: 'e-copedia',
    },
    [NotificationType.DocumentReceived]: {
        name: `NOTIFICATIONS_TYPES_DOCUMENT_RECEIVED_NAME`,
        icon: 'inbox',
        description: t`Get a notification when you receive a document from e-mail`,
        getRoute: () => '/inbox',
        textId: 'NOTIFICATIONS_TYPES_DOCUMENT_RECEIVED_INFO',
        textIdPlural: 'NOTIFICATIONS_TYPES_DOCUMENT_RECEIVED_PLURAL_INFO',
    },
    [NotificationType.BankImportCompleted]: {
        name: `NOTIFICATIONS_TYPES_BANK_IMPORT_COMPLETED_NAME`,
        icon: 'accounting',
        description: t`Get a notification when there are new transactions in Bank reconciliation`,
        getRoute: () => '/accounting/bank/reconciliation',
        textId: 'NOTIFICATIONS_TYPES_BANK_IMPORT_COMPLETED_INFO',
        textIdPlural: 'NOTIFICATIONS_TYPES_BANK_IMPORT_COMPLETED_PLURAL_INFO',
    },
    [NotificationType.NoteFollowUpDateReached]: {
        name: `NOTIFICATIONS_TYPES_NOTE_FOLLOW_UP_NAME`,
        icon: 'document',
        description: t`Get a notification when the follow-up date for note has been exceeded`,
        getRoute: () => '/user/notes/follow-ups',
        textId: 'NOTIFICATIONS_TYPES_NOTE_FOLLOW_UP_INFO',
        textIdPlural: 'NOTIFICATIONS_TYPES_NOTE_FOLLOW_UP_PLURAL_INFO',
    },
    [NotificationType.SalesInvoiceOverdue]: {
        name: `NOTIFICATIONS_TYPES_SALES_INVOICE_OVERDUE_NAME`,
        icon: 'document-time',
        description: t`Get a notification when the due date for a sales invoice has been exceeded`,
        getRoute: () => '/sales/invoicing/reminders',
        getTextId: (notification: ClientNotification) => {
            if (!notification.data || (notification.data as any).invoiceCount === 1) {
                return 'NOTIFICATIONS_TYPES_SALES_INVOICE_OVERDUE_INFO';
            }
            return 'NOTIFICATIONS_TYPES_SALES_INVOICE_OVERDUE_PLURAL_INFO';
        },
        textIdPlural: 'NOTIFICATIONS_TYPES_SALES_INVOICE_OVERDUE_GROUPED_INFO',
    },
    [NotificationType.RequestedDocumentUploaded]: {
        name: `NOTIFICATIONS_TYPES_REQUESTED_DOCUMENT_UPLOADED_NAME`,
        icon: 'document-received',
        description: t`Get a notifcation when you receive a document (Doc. request)`,
        textId: 'NOTIFICATIONS_TYPES_REQUESTED_DOCUMENT_UPLOADED_INFO',
        textIdPlural: 'NOTIFICATIONS_TYPES_REQUESTED_DOCUMENT_UPLOADED_PLURAL_INFO',
        getRoute: (notification: ClientNotification) => {
            const data = notification.data as any;
            if (data.AutoApproveDocuments === true) {
                if (data.IsBookedEntry) {
                    return `/accounting/lists/entries`;
                }
                return `/accounting/journals/${data.JournalId}`;
            }
            return `/accounting/document-requests/review/${data.DocumentRequestId}`;
        },
        getLegacyPageRedirect: (notification: ClientNotification) => {
            const data = notification.data as any;
            if (data?.IsBookedEntry && data.AutoApproveDocuments) {
                const { setupId } = useCurrentAgreement();
                let legacyPageRedirect = `${process.env.ECONOMIC_PATH}/generelt/dataedit.asp?form=105&ops=${setupId}`;
                if (data.VoucherNumber) {
                    legacyPageRedirect += `&Bilagnr=${data.VoucherNumber}&BilagnrE=${data.VoucherNumber}`;
                }
                return legacyPageRedirect;
            }
            return null;
        },
    },
    [NotificationType.DocumentRequestRejected]: {
        name: `NOTIFICATIONS_TYPES_DOCUMENT_REQUEST_REJECTED_NAME`,
        icon: 'document-rejected-request',
        description: t`Get a notification when a document has been rejected (Doc. request)`,
        getRoute: (notification: ClientNotification) => {
            const data = notification.data as any;
            return `/accounting/document-requests/entries-without-documents?fromVoucher=${data.VoucherNumber}&toVoucher=${data.VoucherNumber}&useBookedEntries=${data.UseBookedEntries}`;
        },
        textId: 'NOTIFICATIONS_TYPES_DOCUMENT_REQUEST_REJECTED_INFO',
        textIdPlural: 'NOTIFICATIONS_TYPES_DOCUMENT_REQUEST_REJECTED_PLURAL_INFO',
    },
    [NotificationType.PurchaseInvoiceOverdue]: {
        name: `NOTIFICATIONS_TYPES_PURCHASE_INVOICE_OVERDUE_NAME`,
        icon: 'document-time',
        description: t`Get a notification when the due date for a supplier invoice overdue has been exceeded`,
        getRoute: () => '/reports/suppliers/aged-creditors',
        getTextId: (notification: ClientNotification) => {
            if (!notification.data || (notification.data as any).invoiceCount === 1) {
                return 'NOTIFICATIONS_TYPES_PURCHASE_INVOICE_OVERDUE_INFO';
            }
            return 'NOTIFICATIONS_TYPES_PURCHASE_INVOICE_OVERDUE_PLURAL_INFO';
        },
        textIdPlural: 'NOTIFICATIONS_TYPES_PURCHASE_INVOICE_OVERDUE_GROUPED_INFO',
    },
    [NotificationType.SalesInvoiceSentError]: {
        name: `NOTIFICATIONS_TYPES_SALES_INVOICE_SENT_ERROR_NAME`,
        icon: 'close',
        description: t`Get a notification when a sales invoice has failed during dispatch`,
        getRoute: () => '/sales/invoicing/sent',
        textId: 'NOTIFICATIONS_TYPES_SALES_INVOICE_SENT_ERROR_INFO',
        textIdPlural: 'NOTIFICATIONS_TYPES_SALES_INVOICE_SENT_ERROR_GROUPED_INFO',
    },
    [NotificationType.NewInvoiceFromEconomic]: {
        name: `NOTIFICATIONS_TYPES_NEW_INVOICE_FROM_ECONOMIC_NAME`,
        icon: 'document',
        description: t`Get a notification when the follow-up date for note has been exceeded`,
        getRoute: () => '/settings/subscription/status',
        textId: 'NOTIFICATIONS_TYPES_NEW_INVOICE_FROM_ECONOMIC_INFO',
        textIdPlural: 'NOTIFICATIONS_TYPES_NEW_INVOICE_FROM_ECONOMIC_GROUPED_INFO',
    },
    [NotificationType.ElectronicDocumentReceived]: {
        name: `NOTIFICATIONS_TYPES_ELECTRONIC_DOCUMENT_RECEIVED`,
        icon: 'inbox-einvoicing',
        description: t`Get a notification when you receive an e-invoice`,
        getRoute: () => '/settings/subscription/status',
        // TODO: Fow now there is no translation because we don't support web notifications
        textId: 'PLACEHOLDER',
        textIdPlural: 'PLACEHOLDER',
    },
    [NotificationType.DocumentSentForApproval]: {
        name: `NOTIFICATIONS_TYPES_DOCUMENTS_SENT_FOR_APPROVAL`,
        icon: 'envelope-approved',
        description: t`Get a notification when a document is awaiting your approval`,
        getRoute: () => '/settings/subscription/status',
        // TODO: Fow now there is no translation because we don't support web notifications
        textId: 'PLACEHOLDER',
        textIdPlural: 'PLACEHOLDER',
    },
});

const replaceText = (textId: string, data: object) => {
    let text = oldT(textId);

    if (data) {
        for (const key of Object.keys(data)) {
            text = text.replace(`[${key.toUpperCase()}]`, data[key]);
        }
    }

    return text;
};

export const getNotificationText = (notification: ClientNotification, groupTotal: number | undefined = undefined) => {
    const details = getNotificationDetails()[notification.type];

    let text;

    if (notification.content?.text) {
        text = notification.content?.text;
    } else if (groupTotal && details.textIdPlural) {
        text = replaceText(details.textIdPlural, {
            TOTAL: groupTotal,
        });
    } else if (details.getTextId) {
        text = replaceText(details.getTextId(notification), notification.data);
    } else if (details.textId) {
        text = replaceText(details.textId, notification.data);
    }

    return text;
};

export const getNotificationClickHandler = (notification: ClientNotification, navigate) => {
    const details = getNotificationDetails()[notification.type];
    const url = notification.content?.href || details.getRoute?.(notification);
    const legacyPageRedirect = details.getLegacyPageRedirect ? details.getLegacyPageRedirect(notification) : null;
    let handleClick;

    if (url) {
        handleClick = () => {
            if (url.startsWith('http')) {
                window.open(url, '__blank');
            } else {
                navigate(url);
                /*
                 * Needs a small waiting time redirect the legacy page after navigating the client
                 */
                if (legacyPageRedirect)
                    setTimeout(() => {
                        redirectToLegacyPage(legacyPageRedirect);
                    }, 100);
            }
        };
    }

    return handleClick;
};
